import React from 'react';
import { motion } from 'framer-motion';

const SingpassProjectDetails = ({ project }) => {
  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
      {/* Challenges & Highlights - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Project Challenges */}
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-red-400">🔴 Challenges</h3>
          <div className="space-y-2">
            {project.details.challenges.map((challenge, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{challenge}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Project Highlights */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-5">
          <h3 className="text-lg font-bold mb-3 text-green-400">🟢 Highlights</h3>
          <div className="space-y-2">
            {project.details.highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-2"
              >
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5"></div>
                <span className="text-sm text-gray-200">{highlight}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Milestones */}
      <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4 text-blue-400">📅 2025 Upgrade Milestones</h3>
        <div className="space-y-4">
          {project.details.milestones.map((milestone, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.2 }}
              className="relative"
            >
              {/* Timeline Line */}
              {index < project.details.milestones.length - 1 && (
                <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-cyan-500"></div>
              )}

              <div className="flex gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-2xl shadow-lg z-10">
                  {milestone.icon}
                </div>

                {/* Content */}
                <div className="flex-1 bg-white/5 rounded-lg p-4 border border-blue-500/20 hover:border-blue-500/40 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-cyan-400 bg-cyan-500/20 px-2 py-0.5 rounded">
                      {milestone.date}
                    </span>
                    <span className="text-xs text-blue-300">•</span>
                    <span className="text-xs text-blue-300">{milestone.duration}</span>
                  </div>
                  <h4 className="text-base font-bold text-white mb-1">{milestone.title}</h4>
                  <p className="text-sm text-gray-400">{milestone.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
          <p className="text-green-400 text-sm font-bold">
            ✓ Seamless upgrades with zero downtime • Continuous improvement
          </p>
        </div>
      </div>

      {/* Security & Data Protection */}
      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-3 text-purple-400">🔒 Security & Data Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {project.details.security.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-start gap-2 bg-purple-500/5 p-3 rounded-lg"
            >
              <span className="text-purple-400 text-lg">🛡️</span>
              <span className="text-sm text-gray-200">{item}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Poker-Style Image Stack */}
      <div className="bg-white/5 rounded-xl p-5">
        <h3 className="text-lg font-bold mb-4 text-center">📋 Integration Architecture (Hover to Expand)</h3>
        <div className="relative h-[500px] mx-auto" style={{ maxWidth: '700px' }}>
          {project.details.images.map((image, index) => {
            const offset = index * 25;
            const rotation = -5 + (index * 5);
            const zIndex = 5 - index;

            const getImageLabel = (src) => {
              if (src.includes('myinfo_overview')) return 'MyInfo Overview';
              if (src.includes('myinfo_auth')) return 'Authentication Flow';
              if (src.includes('myinfo_component')) return 'Component Architecture';
              if (src.includes('myinfo_flow')) return 'Data Flow Diagram';
              if (src.includes('myinfo_jwks')) return 'JWKS Implementation';
              return 'Architecture Diagram';
            };

            return (
              <motion.div
                key={image}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  y: offset,
                  rotate: rotation
                }}
                transition={{
                  opacity: { delay: index * 0.1, duration: 0.3 },
                  y: { type: 'spring', stiffness: 200, damping: 20 },
                  rotate: { type: 'spring', stiffness: 200, damping: 20 }
                }}
                whileHover={{
                  y: offset - 30,
                  rotate: rotation,
                  scale: 1.08,
                  zIndex: 100,
                  transition: { duration: 0.2 }
                }}
                className="absolute left-0 right-0 mx-auto"
                style={{
                  width: '85%',
                  maxWidth: '600px',
                  transformOrigin: 'center bottom',
                  cursor: 'pointer',
                  zIndex: index === 2 ? 10 : zIndex
                }}
              >
                <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl border-2 border-purple-500/30 hover:border-purple-500/60 transition-colors">
                  <img
                    src={image}
                    alt={`Architecture ${index + 1}`}
                    className="w-full h-auto object-contain"
                  />
                  <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 px-3 py-2">
                    <p className="text-xs text-center text-gray-300 font-medium">
                      {getImageLabel(image)}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-sm text-gray-400 mt-4">
          Hover over cards to expand • Cards show integration architecture layers
        </p>
      </div>
    </div>
  );
};

export default SingpassProjectDetails;
